import { useEffect, useSyncExternalStore } from 'react';
import { backendApi } from './api';

/**
 * Which support tickets have a customer message THIS staff member has not
 * opened. One store feeds both dots (the Support nav item and the ticket rows)
 * so they cannot drift. Same shape as user-app's useSupportUnread: module
 * state + useSyncExternalStore, a poll that runs only while somebody is
 * subscribed and the tab is visible, and an explicit refresh after a mutation.
 * The read state itself is per staff member and lives on the backend.
 */

const POLL_MS = 60_000;

export interface SupportUnreadState { ticketIds: readonly string[]; loaded: boolean }

const EMPTY: SupportUnreadState = Object.freeze({ ticketIds: Object.freeze([]) as readonly string[], loaded: false });

let state: SupportUnreadState = EMPTY;
const listeners = new Set<() => void>();
let pollers = 0;
let timer: ReturnType<typeof setInterval> | null = null;
let inFlight: Promise<void> | null = null;
/** Bumped by every local change so a slower, older fetch cannot put back a dot just cleared. */
let generation = 0;

const emit = () => { for (const l of listeners) l(); };
const subscribe = (fn: () => void) => { listeners.add(fn); return () => { listeners.delete(fn); }; };
const getSnapshot = () => state;

export function refreshSupportUnread(): Promise<void> {
  if (inFlight) return inFlight;
  const startedAt = generation;
  inFlight = (async () => {
    try {
      const d = await backendApi.get<{ ticketIds?: unknown }>('/tickets/admin/unread');
      if (startedAt !== generation) return;
      const ids = Array.isArray(d?.ticketIds) ? d.ticketIds.filter((x): x is string => typeof x === 'string') : [];
      state = { ticketIds: ids, loaded: true };
      emit();
    } catch {
      /* Older backend (no endpoint) or a blip: keep the last answer; rows fall back to has_unread. */
    } finally { inFlight = null; }
  })();
  return inFlight;
}

/** This staff member opened the ticket: the dot goes at once, then the store re-reads. */
export function markSupportTicketRead(ticketId: string): Promise<void> {
  generation++;
  if (state.ticketIds.includes(ticketId)) {
    state = { ...state, ticketIds: state.ticketIds.filter((id) => id !== ticketId) };
    emit();
  }
  return Promise.resolve().then(() => backendApi.patch(`/tickets/admin/${encodeURIComponent(ticketId)}/read`))
    .catch(() => undefined)
    .then(() => { generation++; return (inFlight ?? Promise.resolve()).then(() => refreshSupportUnread()); });
}

function startPolling() {
  pollers++;
  if (timer) return;
  timer = setInterval(() => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
    void refreshSupportUnread();
  }, POLL_MS);
}
function stopPolling() {
  pollers = Math.max(0, pollers - 1);
  if (pollers > 0 || !timer) return;
  clearInterval(timer); timer = null;
}

/** `enabled` is the `support` read grant: no grant, no fetch, no dot. */
export function useSupportUnread(enabled = true): SupportUnreadState {
  const snap = useSyncExternalStore(subscribe, getSnapshot, () => EMPTY);
  useEffect(() => {
    if (!enabled) return;
    void refreshSupportUnread();
    startPolling();
    const onFocus = () => { void refreshSupportUnread(); };
    window.addEventListener('focus', onFocus);
    return () => { window.removeEventListener('focus', onFocus); stopPolling(); };
  }, [enabled]);
  return enabled ? snap : EMPTY;
}

/** Whether a row wears the dot: the shared store once it has answered, else the list's own flag. */
export function rowHasUnread(row: { id: string; has_unread?: boolean }, unread: SupportUnreadState): boolean {
  return unread.loaded ? unread.ticketIds.includes(row.id) : row.has_unread === true;
}

export function resetSupportUnreadStore(): void {
  state = EMPTY; generation = 0; inFlight = null; pollers = 0;
  if (timer) clearInterval(timer);
  timer = null;
}
